import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap, tap, toArray } from 'rxjs';
import { GungBase } from '../../models';
import { AuthService } from '../auth/auth.service';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { DownloadService } from '../download/download.service';
import { GungNotificationService, PresignedUrlRequest, S3FileRelation, S3UploadService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
export interface DigitalAsset extends GungBase {
  dateLastUpdate: number;
  i18n: { [s: string]: any };
  images: DigitalAssetFile[];
  documents: DigitalAssetFile[];
  /*
  extra: {
    brand: string;
    season: string;
    roles: string;
    tags: string;
    parentId: string;
    type: string; ('folder')
    content: string; ('folder')
  }
  */
}

export interface DigitalAssetFile extends GungBase {
  category: string;
  description: any;
  i18n: { [s: string]: any };
  index: any;
  pimDimensionIds: any[];
  s3ETag: string;
  s3Uri: string;
  s3lastModifiedDate: any;
}
export interface DigitalAssetGroupCascadeChange {
  id: string,
  name: string,
  oldGroups: string;
  newGroups: string;
  becomesEmpty: boolean; // True when delta empties the groups of the item -> visible to everyone(!)
}

@Injectable({
  providedIn: 'root'
})
export class DigitalAssetsService {
  public showFlowsAsDigitalAssets = false;

  protected baseUrl = 'json/pim/digitalassets';
  protected baseUrlImages = 'json/pim/digitalasset-images';
  protected baseUrlDocuments = 'json/pim/digitalasset-documents';

  protected notificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);

  private digitalAssetSubject: BehaviorSubject<DigitalAsset[]>;

  public useLinks = false;

  // Cascading group change propagation flags (Change parent folder groups -> Children groups get changes)
  public cascadeGroupsToDescendants = false;
  public cascadeGroupsDryRun = false;  // Console logging without actually writing

  constructor(
    protected http: HttpClient,
    protected authService: AuthService,
    protected downloadService: DownloadService,
    protected s3Upload: S3UploadService
  ) {}

  public getDigitalAssets(nocache?: boolean): Observable<DigitalAsset[]> {
    if (!this.digitalAssetSubject) {
      this.digitalAssetSubject = new BehaviorSubject<DigitalAsset[]>([]);
      // Added return to fix pipe first() call
      return this.updateDigitalAssetSubject(nocache);
    }
    if(nocache) {
      return this.updateDigitalAssetSubject(nocache);
    }
    return this.digitalAssetSubject.asObservable();
  }

  public getDigitalAsset(id: string, nocache?: boolean): Observable<DigitalAsset> {
    const url = `${this.baseUrl}/${id}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<DigitalAsset>(url, { headers });
  }

  protected internalGetDigitalAssets(nocache?: boolean): Observable<DigitalAsset[]> {
    const url = `${this.baseUrl}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<DigitalAsset[]>(url, { headers });
  }

  updateDigitalAssetSubject(nocache?: boolean): Observable<DigitalAsset[]> {
    return this.internalGetDigitalAssets(nocache).pipe(
      mergeMap(digitalAssets =>
        forkJoin([
          of(digitalAssets),
          this.authService.getRoles().pipe(first()),
          this.authService.getCurrentUser().pipe(first())
        ])
      ),
      map(([digitalAssets, roles, user]) => {
        if (roles.indexOf('ADMIN') === -1) {
          // Filter by roles
          // digitalAssets = digitalAssets.filter(d => {
          //   if (d.extra.roles && d.extra.roles.split(',').length > 0) {
          //     const digitalRoles: string[] = d.extra.roles.split(',').map(r => r.trim());
          //     return digitalRoles.some(r => roles.includes(r.toLocaleUpperCase()));
          //   }
          //   return true;
          // });
          // Filter by groups
          const groupsObject = (user as any).activeGroups;
          const groups = Object.keys(groupsObject).filter(g => groupsObject[g]);
          digitalAssets = digitalAssets.filter(d => {
            if (d.extra.groups && d.extra.groups.split(',').length > 0) {
              const digitalGroups: string[] = d.extra.groups.split(',').map(r => r.trim());
              return digitalGroups.some(r => groups.includes(r.toLocaleUpperCase()));
            }
            return true;
          });
        }
        return digitalAssets;
      }),
      switchMap((data: DigitalAsset[]) => {
        if (this.digitalAssetSubject) {
          this.digitalAssetSubject.next(data);
        }
        return this.digitalAssetSubject || of(data);
      })
      // ).subscribe((data: DigitalAsset[]) => { if (this.digitalAssetSubject) { this.digitalAssetSubject.next(data); } });
    );
  }

  public sendDigitalAssetsFiles(digitalAssets: DigitalAsset[], gungModalService: GungModalService, userEmail: string) {
    if (digitalAssets.length === 0) {
      return;
    }
    const imageS3Uris: string[] = [];
    const documentsToSend: DigitalAssetFile[] = [];
    for (const item of digitalAssets) {
      if (item.images && item.images.length > 0) {
        //      imageS3Uris.push(...item.images.map(image => 'fit-in/5000x5000/filters:quality(100):background_color(white)/' + image.s3Uri)); // Don't remove the fit-in, prevents image size cap from being reached
        imageS3Uris.push(...item.images.map(image => image.s3Uri)); // for urls without fit-in we will use cdn2.gung.io that does not use scaling function, hence no image size cap.
      }
      if (item.documents && item.documents.length > 0) {
        documentsToSend.push(...item.documents);
      }
    }
    gungModalService.openConfirmExportModal(userEmail || '').then(({ email }) => {
      if (email && (email as string).trim().length > 0) {
        this.downloadService.postGenerateAssetsZipFromUrlList(email, imageS3Uris, documentsToSend).subscribe();
      }
    });
  }

  public sendDigitalAssetsFilesUri(
    imageS3Uris: string[],
    documentsToSend: DigitalAssetFile[],
    gungModalService: GungModalService,
    userEmail: string
  ) {
    if (imageS3Uris?.length === 0 && documentsToSend?.length === 0) {
      return;
    }

    gungModalService.openConfirmExportModal(userEmail || '').then(({ email }) => {
      if (email && (email as string).trim().length > 0) {
        this.downloadService
          .postGenerateAssetsZipFromUrlList(email, imageS3Uris || [], documentsToSend || [])
          .subscribe();
      }
    });
  }

  public postDigitalAsset(digitalAsset: DigitalAsset): Observable<DigitalAsset> {
    const url = `${this.baseUrl}`;

    return this.http
      .post<DigitalAsset>(url, digitalAsset)
      .pipe(tap(_ => this.updateDigitalAssetSubject(true).subscribe(result => result)));
  }

  public putDigitalAsset(digitalAsset: DigitalAsset): Observable<DigitalAsset> {
    const url = `${this.baseUrl}/${digitalAsset.id}`;
    return this.http
      .put<DigitalAsset>(url, digitalAsset)
      .pipe(tap(_ => this.updateDigitalAssetSubject(true).subscribe(result => result)));
  }

  public putDigitalAssetQuiet(digitalAsset: DigitalAsset): Observable<DigitalAsset> {
    const url = `${this.baseUrl}/${digitalAsset.id}`;
    return this.http.put<DigitalAsset>(url, digitalAsset);
  }

  public deleteDigitalAsset(digitalAssetId: string): Observable<DigitalAsset> {
    const url = `${this.baseUrl}/${digitalAssetId}`;
    return this.http
      .delete<DigitalAsset>(url)
      .pipe(tap(_ => this.updateDigitalAssetSubject(true).subscribe(result => result)));
  }

  public postDigitalAssetDocuments(digitalAssetId: string, dataImg: FormData): Observable<DigitalAsset> {
    const url = `${this.baseUrlDocuments}/upload/${digitalAssetId}`;
    return this.http.post<DigitalAsset>(url, dataImg);
  }

  public postDigitalAssetImages(digitalAssetId: string, dataImg: FormData): Observable<DigitalAsset> {
    const url = `${this.baseUrlImages}/upload/${digitalAssetId}`;
    return this.http.post<DigitalAsset>(url, dataImg);
  }

  public deleteDigitalAssetImages(digitalAssetId: string, img: DigitalAssetFile): Observable<DigitalAsset> {
    const url = `${this.baseUrlImages}/delete/${digitalAssetId}`;
    return this.http.post<DigitalAsset>(url, img);
  }

  uploadFile(file: File, digitalAssetId: string): Observable<S3FileRelation> {
    const randon = this.generateRandomString(8);
    let request: PresignedUrlRequest = {
      key: `digital-assets/${digitalAssetId}/${randon}/${file.name}`,
      appendCustomerPrefix: true,
      publiclyAccessible: true
    };

    return this.s3Upload.presignedUrlUpload(request).pipe(
      first(),
      switchMap(s3Url => forkJoin({
        s3Url: of(s3Url),
        etag: this.s3Upload.uploadFile(file, s3Url.url).pipe(first())
      })),
      switchMap(({ s3Url, etag }) => {
        const payload = {
          s3Uri: s3Url.s3Key,
          s3Etag: etag,
          s3Bucket: s3Url.s3Bucket,
          relationIds: ['digitalAsset:' + digitalAssetId],
          documentType: 'digitalAsset'
        };
        return this.s3Upload.createFileRelation(payload).pipe(first());
      })
    );
  }

  sortImages(files: File[]): File[] {
    return files;
  }

  generateRandomString(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  }

  // Cascade feature
  public cascadeDigitalAssetGroups(
    folderId: string,
    oldGroups: string,
    newGroups: string
  ): Observable<unknown> {
    if (!this.cascadeGroupsToDescendants) {
      return of(null);
    }

    const split = (groups: string) =>
      (groups || '')
        .split(',')
        .map(g => g.trim())
        .filter(g => !!g);
    
    const before = split(oldGroups);
    const after = split(newGroups);
    const added = after.filter(g => before.indexOf(g) === -1);
    const removed = before.filter(g => after.indexOf(g) === -1);
    if (added.length === 0 && removed.length === 0) {
      return of(null);
    }

    const apply = (groups: string) => {
      const result = split(groups).filter(g => removed.indexOf(g) === -1);
      added.forEach(g => result.indexOf(g) === -1 && result.push(g));
      return result.join(',');
    };

    return this.getDigitalAssets().pipe(
      first(),
      switchMap(all => {
        const ids = [folderId];
        const affected: DigitalAssetGroupCascadeChange[] = [];
        for (let i=0; i<ids.length; i++) {
          for (const child of all.filter(a => a.extra?.parentId === ids[i])) {
            if (ids.indexOf(child.id) > -1) {
              continue;
            }
            ids.push(child.id);
            const current = split(child.extra?.groups).join(',');
            const next = apply(child.extra?.groups);
            if (next !== current) {
              affected.push({
                id: child.id,
                name: child.name,
                oldGroups: current,
                newGroups: next,
                becomesEmpty: next === '' && current !== ''
              });
            }
          }
        }

        if (affected.length === 0) {
          return of(null);
        }

        if (this.cascadeGroupsDryRun) {
          console.log(
            '[digital-assets] DRY RUN cascade from ' + folderId +
            ' - added: [' + added.join(',') + '] removed: [' + removed.join(',') + '] - ' +
            affected.length + ' item(s) would be written, ' +
            affected.filter(c => c.becomesEmpty).length + ' would end up with NO groups'
          );
          console.table(affected);
          return of(null);
        }

        const failed: DigitalAssetGroupCascadeChange[] = [];

        return from(affected).pipe(
          mergeMap(
            change =>
              this.getDigitalAsset(change.id, true).pipe(
                first(),
                switchMap(fresh =>
                  this.putDigitalAssetQuiet({
                    ...fresh,
                    dateLastUpdate: new Date().getTime(),
                    images: (fresh.images || []).filter(f => !f.extra?.isPreview),
                    documents: (fresh.documents || []).filter(f => !f.extra?.isPreview),
                    extra: { ...fresh.extra, groups: apply(fresh.extra?.groups) }
                  })
                ),
                catchError(error => {
                  console.error('[digital-assets] cascade failed for ' + change.id, error);
                  failed.push(change);
                  return of(null);
                })
              ),
            4
          ),
          toArray(),
          tap(() => this.reportCascadeFailures(affected.length, failed)),
          switchMap(() => this.updateDigitalAssetSubject(true).pipe(first()))
        );
      })
    );
  }

  protected reportCascadeFailures(total: number, failed: DigitalAssetGroupCascadeChange[]): void {
    if (failed.length === 0) {
      return;
    }
    const names = 
      failed.slice(0, 5).map(f => f.name || f.id).join(', ') + (failed.length > 5 ? ', ...' : '');
    this.notificationService.notify(
      this.translateService.instant('ERROR'),
      this.translateService.instant('DIGITAL_ASSETS_CASCADE_FAILED', { failed: failed.length, total, names }),
      'error',
      undefined,
      15000
    );
  }

}
